import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings, Shield, ChevronRight } from 'lucide-react';
import { useCookieConsent } from '../../context/CookieConsentContext';

export const CookieBanner = () => {
  const { showBanner, acceptAll, acceptNecessaryOnly, openSettings } = useCookieConsent();
  
  // Don't show banner on print page (used for PDF generation)
  // Check pathname directly to avoid useLocation issues
  if (typeof window !== 'undefined' && window.location.pathname === '/print') {
    return null;
  }

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#1a2235]/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl shadow-black/30 overflow-hidden">
              {/* Header accent line */}
              <div className="h-1 bg-gradient-to-r from-emerald via-teal-400 to-blue-500" />
              
              <div className="p-6 md:p-8">
                {/* Content */}
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  {/* Icon */}
                  <div className="flex-shrink-0 hidden md:block">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald/20 to-teal-500/10 border border-emerald/20 flex items-center justify-center">
                      <Cookie className="w-7 h-7 text-emerald" />
                    </div>
                  </div>
                  
                  {/* Text */}
                  <div className="flex-1">
                    <h3 className="font-outfit text-lg md:text-xl font-semibold text-white mb-2 flex items-center gap-3">
                      <Cookie className="w-5 h-5 text-emerald md:hidden" />
                      Deine Privatsphäre ist mir wichtig
                    </h3>
                    <p className="text-white/60 text-sm md:text-base leading-relaxed">
                      Diese Seite verwendet Cookies, um dir das beste Erlebnis zu bieten. 
                      Technisch notwendige Cookies sind für die Grundfunktionen erforderlich. 
                      Statistik-Cookies helfen mir, die Website zu verbessern.
                    </p>
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button
                    onClick={acceptAll}
                    className="flex-1 px-6 py-3.5 bg-emerald hover:bg-emerald/90 text-white rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-emerald/20"
                    data-testid="cookie-accept-all-btn"
                  >
                    <Shield className="w-4 h-4" />
                    Alle akzeptieren
                  </button>
                  
                  <button
                    onClick={acceptNecessaryOnly}
                    className="flex-1 px-6 py-3.5 bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 rounded-xl font-medium text-sm transition-all duration-200"
                    data-testid="cookie-necessary-only-btn"
                  >
                    Nur notwendige
                  </button>
                  
                  <button
                    onClick={openSettings}
                    className="flex-1 sm:flex-initial px-6 py-3.5 text-white/70 hover:text-white rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 hover:bg-white/5"
                    data-testid="cookie-settings-btn"
                  >
                    <Settings className="w-4 h-4" />
                    Einstellungen
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
